import { Shop } from '../../../screens/Main/Shop/Shop';
import { ProductDetail } from '../../../screens/Main/ProductDetail';
import { Orders } from '../../../screens/Main/Orders';
import { ContactUs } from '../../../screens/Main/ContactUs';
import { ScreenComponent } from '../../../models/enums/ScreenComponents';
import { Cart } from '../../../screens/Main/Cart';
import { useLinkTo } from '@react-navigation/native';
import { navigationStore } from '../../stores/Navigation';
import { PortalRoutes } from '../../../screens/Portal/PortalRoutes';
import { ThemePreview } from '../../../screens/Other/ThemePreview';
import trackingService, { ActivityType } from '../../../services/analytics/TrackingService';
// Custom screens - import from registry
import { getCustomScreenComponent } from '../../../screens/Main/Custom';

export const useScreenComponent = () => {
	const linkTo = useLinkTo();
	const { updateCurrentScreen, updateProfileDrawerOpen, screenRoutes } = navigationStore((state) => state);

	const navigateToScreen = (screen: ScreenComponent, params?: Object) => {
		// Handle Login/Register by opening profile drawer instead
		if (screen === ScreenComponent.Login || screen === ScreenComponent.Register) {
			updateProfileDrawerOpen(true);
			return;
		}

		// Use custom route from database if available, otherwise derive from screenKey
		const customRoute = screenRoutes[screen];
		const screenPath = customRoute || screen.toLowerCase().replaceAll('_', '');
		const screenParams = params ? Object.entries(params).map(([key, value]) => `${key}=${value}`).join('&') : '';
		const screenName = screen.replaceAll('_', ' ').toString();
		
		linkTo(`/${screenPath}?${screenParams}`);
		updateCurrentScreen(screen as ScreenComponent);

		// Track navigation
		trackingService.trackActivity({
			activityType: ActivityType.NAVIGATION,
			screenName: screenName,
			screenPath: `/${screenPath}`,
			activityData: params || {}
		}).catch(error => {
			console.error('Failed to track navigation:', error);
		});
	};

	const openProfileDrawer = () => {
		updateProfileDrawerOpen(true);
	};

	// function to return screen component based on screen enum
	const getScreenComponent = (screen: ScreenComponent) => {
		// Core screens
		switch (screen) {
			case ScreenComponent.Shop:
				return Shop;
			case ScreenComponent.Product:
				return ProductDetail;
			case ScreenComponent.Orders:
				return Orders;
			case ScreenComponent.Contact:
				return ContactUs;
			case ScreenComponent.Cart:
				return Cart;
			case ScreenComponent.Portal:
				return PortalRoutes;
			case ScreenComponent.ThemePreview:
				return ThemePreview;
			// Login and Register are handled via profile drawer, not as separate screens
			case ScreenComponent.Login:
			case ScreenComponent.Register:
				return Shop;
			default:
				// Try to get from custom screen registry
				const customComponent = getCustomScreenComponent(screen);
				if (customComponent) {
					return customComponent;
				}
				// Fallback to Shop for any unknown/removed screens
				console.warn(`Unknown screen component: ${screen}, falling back to Shop`);
				return Shop;
		}
	}

	return { getScreenComponent, navigateToScreen, openProfileDrawer };
};
